/**
 * Animation Configuration Modal
 * Allows users to configure FTS animation parameters
 */
import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
    XMarkIcon,
    PlayIcon,
    Cog6ToothIcon,
} from '@heroicons/react/24/solid';

export interface AnimationConfig {
    frameRate: number;
    playbackSpeed: number;
    showGSE: boolean;
    showTrails: boolean;
    trailLength: number;
    daySelection: 'busiest' | 'quietest' | 'custom';
    customDate?: string;
}

interface DaySummary {
    date: string;
    total_movements: number;
    arrivals: number;
    departures: number;
    is_busiest: boolean;
    is_quietest: boolean;
}

interface AnimationConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (config: AnimationConfig) => void;
    availableDays?: DaySummary[];
    busiestDay?: string;
    quietestDay?: string;
    initialConfig?: Partial<AnimationConfig>;
}

const defaultConfig: AnimationConfig = {
    frameRate: 1,
    playbackSpeed: 1,
    showGSE: true,
    showTrails: true,
    trailLength: 120,
    daySelection: 'busiest',
};

const AnimationConfigModal: React.FC<AnimationConfigModalProps> = ({
    isOpen,
    onClose,
    onApply,
    availableDays = [],
    busiestDay,
    quietestDay,
    initialConfig = {},
}) => {
    const { language } = useLanguage();
    const isEs = (language as string) === 'es';
    const [config, setConfig] = useState<AnimationConfig>({
        ...defaultConfig,
        ...initialConfig,
    });

    if (!isOpen) return null;

    const handleApply = () => {
        onApply(config);
        onClose();
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-ES', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="w-full max-w-lg rounded-xl border border-[var(--border)] bg-white p-6 shadow-2xl">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Cog6ToothIcon className="h-6 w-6 text-[#F26430]" />
                        <h2 className="text-xl font-semibold text-white">
                            {isEs ? 'Configuración de Animación' : 'Animation Configuration'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1 text-[var(--text-secondary)] hover:bg-[var(--surface-inset)] hover:text-[var(--text-primary)]"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Day Selection */}
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-300">
                            {isEs ? 'Día a simular' : 'Day to simulate'}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                onClick={() =>
                                    setConfig({ ...config, daySelection: 'busiest' })
                                }
                                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-all ${config.daySelection === 'busiest'
                                        ? 'border-[#F26430] bg-[#F26430]/20 text-[#F26430]'
                                        : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-gray-500'
                                    }`}
                            >
                                {isEs ? 'Día Más Tráfico' : 'Busiest Day'}
                                {busiestDay && (
                                    <span className="mt-1 block text-xs opacity-70">
                                        {formatDate(busiestDay)}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() =>
                                    setConfig({ ...config, daySelection: 'quietest' })
                                }
                                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-all ${config.daySelection === 'quietest'
                                        ? 'border-[#F26430] bg-[#F26430]/20 text-[#F26430]'
                                        : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-gray-500'
                                    }`}
                            >
                                {isEs ? 'Día Menos Tráfico' : 'Quietest Day'}
                                {quietestDay && (
                                    <span className="mt-1 block text-xs opacity-70">
                                        {formatDate(quietestDay)}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() =>
                                    setConfig({ ...config, daySelection: 'custom' })
                                }
                                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-all ${config.daySelection === 'custom'
                                        ? 'border-[#F26430] bg-[#F26430]/20 text-[#F26430]'
                                        : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-gray-500'
                                    }`}
                            >
                                {isEs ? 'Elegir Día' : 'Choose Day'}
                            </button>
                        </div>

                        {config.daySelection === 'custom' && availableDays.length > 0 && (
                            <select
                                value={config.customDate || ''}
                                onChange={(e) =>
                                    setConfig({ ...config, customDate: e.target.value })
                                }
                                className="mt-3 w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-[var(--text-primary)] focus:border-[#F26430] focus:outline-none"
                            >
                                <option value="">{isEs ? 'Seleccionar día...' : 'Select day...'}</option>
                                {availableDays.map((day) => (
                                    <option key={day.date} value={day.date}>
                                        {formatDate(day.date)} - {day.total_movements} {isEs ? 'movimientos' : 'movements'}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Playback Speed */}
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-300">
                            {isEs ? 'Velocidad de reproducción:' : 'Playback speed:'}{' '}
                            <span className="text-[#F26430]">{config.playbackSpeed}x</span>
                        </label>
                        <div className="flex gap-2">
                            {[1, 2, 4, 10, 30, 60].map((speed) => (
                                <button
                                    key={speed}
                                    onClick={() => setConfig({ ...config, playbackSpeed: speed })}
                                    className={`flex-1 rounded-lg border px-2 py-1.5 text-sm font-medium transition-all ${config.playbackSpeed === speed
                                            ? 'border-[#F26430] bg-[#F26430]/20 text-[#F26430]'
                                            : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-gray-500'
                                        }`}
                                >
                                    {speed}x
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Frame Rate */}
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-300">
                            {isEs ? 'Resolución:' : 'Resolution:'} {config.frameRate} fps
                        </label>
                        <input
                            type="range"
                            min="1"
                            max="10"
                            value={config.frameRate}
                            onChange={(e) =>
                                setConfig({ ...config, frameRate: Number(e.target.value) })
                            }
                            className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-[var(--surface-inset)] accent-[#F26430]"
                        />
                        <div className="mt-1 flex justify-between text-xs text-[var(--text-tertiary)]">
                            <span>1 fps ({isEs ? 'suave' : 'smooth'})</span>
                            <span>10 fps ({isEs ? 'detallado' : 'detailed'})</span>
                        </div>
                    </div>

                    {/* Visual Options */}
                    <div className="grid grid-cols-2 gap-4">
                        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[var(--border)] p-3 hover:border-gray-500">
                            <input
                                type="checkbox"
                                checked={config.showGSE}
                                onChange={(e) =>
                                    setConfig({ ...config, showGSE: e.target.checked })
                                }
                                className="h-4 w-4 rounded border-gray-500 bg-[var(--surface-inset)] text-[#F26430] focus:ring-[#F26430]"
                            />
                            <span className="text-sm text-gray-300">
                                {isEs ? 'Mostrar vehículos GSE' : 'Show GSE vehicles'}
                            </span>
                        </label>

                        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[var(--border)] p-3 hover:border-gray-500">
                            <input
                                type="checkbox"
                                checked={config.showTrails}
                                onChange={(e) =>
                                    setConfig({ ...config, showTrails: e.target.checked })
                                }
                                className="h-4 w-4 rounded border-gray-500 bg-[var(--surface-inset)] text-[#F26430] focus:ring-[#F26430]"
                            />
                            <span className="text-sm text-gray-300">{isEs ? 'Mostrar estelas' : 'Show trails'}</span>
                        </label>
                    </div>

                    {/* Trail Length (only if trails enabled) */}
                    {config.showTrails && (
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-300">
                                {isEs ? 'Longitud de estela:' : 'Trail length:'} {config.trailLength}s
                            </label>
                            <input
                                type="range"
                                min="30"
                                max="300"
                                step="30"
                                value={config.trailLength}
                                onChange={(e) =>
                                    setConfig({ ...config, trailLength: Number(e.target.value) })
                                }
                                className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-[var(--surface-inset)] accent-[#F26430]"
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-8 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="rounded-lg border border-[var(--border)] px-4 py-2 text-gray-300 hover:bg-[var(--surface-inset)]"
                    >
                        {isEs ? 'Cancelar' : 'Cancel'}
                    </button>
                    <button
                        onClick={handleApply}
                        className="flex items-center gap-2 rounded-lg bg-[#F26430] px-4 py-2 font-medium text-white hover:bg-[#d14f1f]"
                    >
                        <PlayIcon className="h-4 w-4" />
                        {isEs ? 'Iniciar Animación' : 'Start Animation'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnimationConfigModal;
